import React from 'react';
import { Box, Container, Typography, Chip, Grid, Card, CardContent, Divider } from '@mui/material';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import EmailIcon from '@mui/icons-material/Email';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import BusinessIcon from '@mui/icons-material/Business';

const PRIMARY = '#FF7518';
const DARK = '#4a2d08';
const DARK_BG = '#0f0f0f';

const channels = [
  {
    icon: <SupportAgentIcon sx={{ fontSize: 32, color: PRIMARY }} />,
    title: 'Technical Support',
    description: 'Questions about scans, rules, CLI usage, or integrations.',
    contact: 'support@baastream.com',
    href: 'mailto:support@baastream.com',
    response: 'Response within 24 hours (Pro/Enterprise: 4 hours)',
  },
  {
    icon: <BusinessIcon sx={{ fontSize: 32, color: PRIMARY }} />,
    title: 'Sales & Enterprise',
    description: 'Custom pricing, volume discounts, enterprise contracts, or demos.',
    contact: 'sales@baastream.com',
    href: 'mailto:sales@baastream.com',
    response: 'Response within 1 business day',
  },
];

export function ContactUs() {
  return (
    <Box sx={{ bgcolor: '#fff', minHeight: '100vh' }}>
      {/* Minimal nav */}
      <Box sx={{ bgcolor: DARK_BG, py: 2, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <Container maxWidth="lg">
          <Box
            component="a"
            href="/"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}
          >
            <HealthAndSafetyIcon sx={{ color: PRIMARY, fontSize: 26 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
              BaaStream
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Hero */}
      <Box sx={{ bgcolor: DARK_BG, py: { xs: 8, md: 12 } }}>
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Chip
            label="Contact Us"
            size="small"
            sx={{ bgcolor: 'rgba(255,117,24,0.15)', color: PRIMARY, fontWeight: 600, mb: 3 }}
          />
          <Typography
            variant="h1"
            sx={{ fontSize: { xs: '2.2rem', md: '3rem' }, fontWeight: 800, color: '#fff', lineHeight: 1.2, mb: 3 }}
          >
            We'd love to{' '}
            <Box component="span" sx={{ color: PRIMARY }}>
              hear from you
            </Box>
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '1.05rem', lineHeight: 1.8, mb: 6 }}>
            Got a question, idea, or just want to say hi? Our inbox is always open.
          </Typography>

          {/* Primary CTA email */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              bgcolor: 'rgba(255,117,24,0.12)',
              border: '1px solid rgba(255,117,24,0.35)',
              borderRadius: 3,
              px: 4,
              py: 2.5,
            }}
          >
            <EmailIcon sx={{ color: PRIMARY, fontSize: 28 }} />
            <Box>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 0.25 }}>
                Write to us at
              </Typography>
              <Box
                component="a"
                href="mailto:connect@baastream.com"
                sx={{
                  color: PRIMARY,
                  fontWeight: 700,
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  textDecoration: 'none',
                  letterSpacing: 0.3,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                connect@baastream.com
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Other channels */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 10 } }}>
        <Typography variant="h2" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.7rem', md: '2rem' }, mb: 1 }}>
          Specific enquiries
        </Typography>
        <Typography variant="body1" sx={{ color: '#666', mb: 5 }}>
          For faster responses, reach the right team directly.
        </Typography>

        <Grid container spacing={3}>
          {channels.map((ch) => (
            <Grid size={{ xs: 12, sm: 6 }} key={ch.title}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 32px rgba(255,117,24,0.1)' },
                }}
              >
                <CardContent sx={{ p: 3.5 }}>
                  <Box sx={{ mb: 2 }}>{ch.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: DARK, mb: 1 }}>
                    {ch.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', lineHeight: 1.7, mb: 2 }}>
                    {ch.description}
                  </Typography>
                  <Box
                    component="a"
                    href={ch.href}
                    sx={{
                      color: PRIMARY,
                      fontWeight: 600,
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {ch.contact}
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', color: '#999', mt: 1.5 }}>
                    {ch.response}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 8 }} />

        {/* Support hours */}
        <Grid container spacing={6}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.4rem', md: '1.7rem' }, mb: 3 }}>
              Support Hours by Plan
            </Typography>
            <Box sx={{ bgcolor: '#fafafa', borderRadius: 2, p: 3, border: '1px solid rgba(0,0,0,0.06)' }}>
              {[
                { plan: 'Community', hours: 'Community forum & GitHub (async)' },
                { plan: 'Starter', hours: 'Email · Response within 24 hours' },
                { plan: 'Pro', hours: 'Priority email · Response within 4 hours' },
                { plan: 'Enterprise', hours: 'Dedicated engineer · SLA-backed' },
              ].map((row) => (
                <Box
                  key={row.plan}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 2,
                    pb: 2,
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    '&:last-child': { mb: 0, pb: 0, borderBottom: 'none' },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 700, color: DARK, minWidth: 90 }}>
                    {row.plan}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#555', textAlign: 'right', lineHeight: 1.5 }}>
                    {row.hours}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: DARK, fontSize: { xs: '1.4rem', md: '1.7rem' }, mb: 3 }}>
              Before you write
            </Typography>
            {[
              {
                q: 'Is there a free plan?',
                a: 'Yes — the Community plan is free forever with 50 scans/month.',
              },
              {
                q: 'Can I get a demo?',
                a: 'Absolutely. Email sales@baastream.com to schedule a live walkthrough.',
              },
              {
                q: 'Who is behind BaaStream?',
                a: 'BaaStream is a product of Monks Do Work. All billing is with Monks Do Work.',
              },
            ].map((faq) => (
              <Box key={faq.q} sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ fontWeight: 700, color: DARK, mb: 0.5 }}>
                  {faq.q}
                </Typography>
                <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.7 }}>
                  {faq.a}
                </Typography>
              </Box>
            ))}

            <Box
              sx={{
                mt: 2,
                p: 3,
                bgcolor: 'rgba(255,117,24,0.05)',
                border: `1px solid rgba(255,117,24,0.2)`,
                borderRadius: 2,
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700, color: DARK, mb: 0.5 }}>
                Healthcare organizations
              </Typography>
              <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.7 }}>
                Verified healthcare orgs get extended free trials and priority onboarding. Mention your organization when writing to us.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Footer */}
      <Box sx={{ bgcolor: DARK_BG, py: 4, mt: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
              © {new Date().getFullYear()} Monks Do Work. All rights reserved.
            </Typography>
            <Box component="a" href="/" sx={{ color: PRIMARY, fontSize: '0.8rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              ← Back to Home
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
